import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  User, Mail, Fingerprint, Crown, CalendarClock, CalendarCheck, Zap, Package, Archive, TrendingUp,
  Image as ImageIcon, Boxes, Layers, Globe2, Camera, LogIn, Activity as ActivityIcon, Bell, Palette,
  Volume2, Shield, Lock, Save, Upload, CreditCard, History, RefreshCw, Sparkles, Loader2, Check,
  Monitor, LogOut, Trash2,
} from "lucide-react";
import { AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { HoloCard, NeonButton, accentColor } from "@/components/Holo";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { estImages } from "@/lib/billing";
import Billing from "@/pages/dashboard/Billing";

const TABS = [
  ["overview", "Overview", User], ["credits", "Credits", Zap], ["activity", "Activity", ActivityIcon],
  ["projects", "Projects", Boxes], ["billing", "Billing", CreditCard], ["notifications", "Notifications", Bell],
  ["settings", "Settings", Shield], ["analytics", "Analytics", TrendingUp],
];
const FEATURE_COLORS = ["#00f0ff", "#ff007f", "#b026ff", "#39ff14", "#eaff00"];
const fileToDataUrl = (f) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";
const fmtDateTime = (d) => d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const chartTip = { contentStyle: { background: "#0b0b0d", border: "1px solid #222", fontFamily: "JetBrains Mono, monospace", fontSize: 11 }, labelStyle: { color: "#9aa" } };

const Stat = ({ icon: I, label, value, accent = "turquoise", testid }) => {
  const c = accentColor(accent);
  return (
    <HoloCard accent={accent} className="p-4" hover={false} testid={testid}>
      <I size={15} style={{ color: c.hex }} className="mb-2" />
      <div className="font-display font-black text-2xl">{value}</div>
      <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40 mt-0.5">{label}</div>
    </HoloCard>
  );
};

export default function Account({ onNavigate }) {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const { user, refreshUser } = useAuth();
  const fileRef = useRef();

  const load = useCallback(() => {
    api.get("/account/summary").then(({ data }) => setData(data)).catch((e) => console.error("account summary failed:", e));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickAvatar = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) return toast.error("Image too large (max 1.5MB)");
    try { const url = await fileToDataUrl(f); await api.patch("/account/profile", { avatar: url }); await refreshUser(); toast.success("Profile picture updated"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const stats = data?.stats || {};
  const charts = data?.charts || { daily: [], features: [], monthly: [] };

  return (
    <div data-testid="module-account" className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-5 flex-wrap">
        <div className="relative group">
          <div className="w-20 h-20 border border-neon-turquoise/40 overflow-hidden bg-black/40 flex items-center justify-center clip-corner" data-testid="account-avatar">
            {user?.avatar ? <img src={user.avatar} alt="avatar" className="w-full h-full object-cover" /> : <User size={30} className="text-white/30" />}
          </div>
          <button onClick={() => fileRef.current?.click()} data-testid="avatar-upload-btn"
            className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"><Upload size={18} className="text-neon-turquoise" /></button>
          <input ref={fileRef} type="file" accept="image/*" hidden onChange={pickAvatar} />
        </div>
        <div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-2">{user?.name} <Crown size={16} className="text-neon-yellow" /></h1>
          <p className="font-mono-tech text-white/40 text-sm mt-1">{user?.email} · {user?.plan} Access Tier</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap border-b border-white/10 pb-3">
        {TABS.map(([k, l, I]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`account-tab-${k}`}
            className={`flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-widest px-3 py-2 transition-colors ${tab === k ? "text-neon-turquoise border-b-2 border-neon-turquoise" : "text-white/45 hover:text-white/80"}`}>
            <I size={13} /> {l}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div className="space-y-6" data-testid="tab-overview">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[[User, "Name", user?.name], [Mail, "Email", user?.email], [Fingerprint, "User ID", user?.id?.slice(-10)],
              [Crown, "Plan", user?.plan], [CalendarClock, "Member Since", fmtDate(user?.created_at)],
              [CalendarCheck, "Renewal", fmtDate(user?.renewal_date)]].map(([I, label, val], i) => (
              <HoloCard key={i} accent="turquoise" className="p-4 flex items-center gap-3" hover={false}>
                <I size={18} className="text-neon-turquoise shrink-0" />
                <div className="min-w-0"><div className="font-mono-tech text-[10px] uppercase text-white/40">{label}</div><div className="font-mono-tech text-sm text-white/90 truncate">{val || "—"}</div></div>
              </HoloCard>
            ))}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={ImageIcon} label="Images Generated" value={stats.total_generations ?? 0} accent="pink" testid="stat-gens" />
            <Stat icon={Globe2} label="Campaigns" value={stats.campaigns ?? 0} accent="purple" />
            <Stat icon={Boxes} label="Characters" value={stats.characters ?? 0} accent="turquoise" />
            <Stat icon={Zap} label="Credits Remaining" value={(user?.credits ?? 0).toLocaleString()} accent="green" />
          </div>
        </div>
      )}

      {/* CREDITS */}
      {tab === "credits" && (
        <div className="space-y-6" data-testid="tab-credits">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Stat icon={Zap} label="Monthly" value={(user?.monthly_credits ?? 0).toLocaleString()} accent="pink" testid="cred-monthly" />
            <Stat icon={Package} label="Purchased" value={(user?.purchased_credits ?? 0).toLocaleString()} accent="green" testid="cred-purchased" />
            <Stat icon={Archive} label="Legacy" value={(user?.legacy_credits ?? 0).toLocaleString()} accent="turquoise" testid="cred-legacy" />
            <Stat icon={TrendingUp} label="Used (all-time)" value={(stats.credits_used ?? 0).toLocaleString()} accent="purple" />
            <Stat icon={Sparkles} label="≈ Generations Left" value={estImages(user?.credits)} accent="yellow" />
          </div>
          <HoloCard accent="green" className="p-6" hover={false}>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">Credit Usage · Last 6 Months</div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={charts.monthly}>
                <defs><linearGradient id="cg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#39ff14" stopOpacity={0.5} /><stop offset="100%" stopColor="#39ff14" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1f" />
                <XAxis dataKey="month" stroke="#555" fontSize={11} /><YAxis stroke="#555" fontSize={11} />
                <Tooltip {...chartTip} />
                <Area type="monotone" dataKey="credits" stroke="#39ff14" fill="url(#cg)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </HoloCard>
          <div className="flex gap-3 flex-wrap">
            <NeonButton accent="green" onClick={() => setTab("billing")} data-testid="credits-buy-btn"><Zap size={13} /> Buy Credits</NeonButton>
            <NeonButton accent="pink" onClick={() => setTab("billing")} data-testid="credits-upgrade-btn"><Crown size={13} /> Upgrade Plan</NeonButton>
            <NeonButton accent="turquoise" onClick={() => setTab("activity")} data-testid="credits-history-btn"><History size={13} /> Billing History</NeonButton>
          </div>
        </div>
      )}

      {/* ACTIVITY */}
      {tab === "activity" && (
        <div className="space-y-6" data-testid="tab-activity">
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <Stat icon={LogIn} label="Last Login" value={fmtDateTime(user?.last_login)} accent="turquoise" />
            <Stat icon={ImageIcon} label="Total Generated" value={stats.total_generations ?? 0} accent="pink" />
            <Stat icon={Globe2} label="Campaigns" value={stats.campaigns ?? 0} accent="purple" />
          </div>
          <HoloCard accent="turquoise" className="p-6" hover={false}>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">Recent Activity Timeline</div>
            <div className="space-y-3">
              {(data?.activity || []).length === 0 ? <div className="font-mono-tech text-white/30 text-sm">No activity yet.</div> :
                (data?.activity || []).map((e, i) => (
                  <div key={i} className="flex items-center gap-3 border-l-2 pl-3 py-1" style={{ borderColor: e.type === "credit" ? "#39ff14" : e.type === "campaign" ? "#b026ff" : "#00f0ff" }} data-testid={`activity-${i}`}>
                    <div className="flex-1"><div className="font-mono-tech text-[12px] text-white/85">{e.label}</div><div className="font-mono-tech text-[10px] text-white/35">{fmtDateTime(e.created_at)}</div></div>
                    {e.status && <span className="font-mono-tech text-[9px] uppercase" style={{ color: e.status === "failed" ? "#ff007f" : "#39ff14" }}>{e.status}</span>}
                  </div>
                ))}
            </div>
          </HoloCard>
        </div>
      )}

      {/* PROJECTS */}
      {tab === "projects" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tab-projects">
          {[["identity", "Identity Matrix™", Boxes, "pink", stats.characters], ["brand", "Brand Core™", Package, "turquoise", stats.products],
            ["scene", "Scene Memory™", Layers, "green", stats.scenes], ["forge", "World Forge™", Globe2, "purple", stats.campaigns],
            ["vault", "Memory Vault™", Archive, "yellow", (stats.characters || 0) + (stats.products || 0) + (stats.scenes || 0)]].map(([key, title, I, accent, count]) => {
            const c = accentColor(accent);
            return (
              <button key={key} onClick={() => onNavigate && onNavigate(key)} data-testid={`project-${key}`}
                className="text-left border border-white/10 p-5 hover:border-white/30 transition-colors group">
                <I size={22} style={{ color: c.hex }} />
                <div className="font-display font-bold text-lg mt-3">{title}</div>
                <div className="font-mono-tech text-[11px] text-white/40 mt-1">{count ?? 0} saved · open <span style={{ color: c.hex }}>→</span></div>
              </button>
            );
          })}
        </div>
      )}

      {/* BILLING (reuse) */}
      {tab === "billing" && <div data-testid="tab-billing"><Billing /></div>}

      {/* NOTIFICATIONS */}
      {tab === "notifications" && <NotificationPrefs user={user} refreshUser={refreshUser} />}

      {/* SETTINGS */}
      {tab === "settings" && <SettingsTab user={user} refreshUser={refreshUser} />}

      {/* ANALYTICS */}
      {tab === "analytics" && (
        <div className="space-y-6" data-testid="tab-analytics">
          <HoloCard accent="pink" className="p-6" hover={false}>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">Generations Per Day · Last 14 Days</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={charts.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1f" />
                <XAxis dataKey="date" stroke="#555" fontSize={10} /><YAxis stroke="#555" fontSize={11} allowDecimals={false} />
                <Tooltip {...chartTip} cursor={{ fill: "rgba(255,0,127,0.08)" }} />
                <Bar dataKey="generations" fill="#ff007f" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </HoloCard>
          <div className="grid lg:grid-cols-2 gap-6">
            <HoloCard accent="purple" className="p-6" hover={false}>
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">Most Used Features</div>
              {charts.features.length === 0 ? <div className="font-mono-tech text-white/30 text-sm">No data yet.</div> : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={charts.features} dataKey="count" nameKey="feature" cx="50%" cy="50%" outerRadius={80} innerRadius={45} paddingAngle={3}>
                      {charts.features.map((_, i) => <Cell key={i} fill={FEATURE_COLORS[i % FEATURE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip {...chartTip} />
                  </PieChart>
                </ResponsiveContainer>
              )}
              <div className="flex flex-wrap gap-2 mt-3">
                {charts.features.map((f, i) => <span key={f.feature} className="font-mono-tech text-[10px] flex items-center gap-1"><span className="w-2 h-2" style={{ background: FEATURE_COLORS[i % FEATURE_COLORS.length] }} />{f.feature.replace(/_/g, " ")} ({f.count})</span>)}
              </div>
            </HoloCard>
            <HoloCard accent="green" className="p-6" hover={false}>
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">Monthly Statistics</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={charts.monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1f" />
                  <XAxis dataKey="month" stroke="#555" fontSize={11} /><YAxis stroke="#555" fontSize={11} />
                  <Tooltip {...chartTip} cursor={{ fill: "rgba(57,255,20,0.08)" }} />
                  <Bar dataKey="credits" fill="#39ff14" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </HoloCard>
          </div>
        </div>
      )}
    </div>
  );
}

const TOGGLES = [
  ["low_credit", "Low Credit Emails"], ["purchase", "Purchase Confirmations"], ["renewal", "Renewal Notifications"],
  ["marketing", "Marketing Emails"], ["character_drift", "Character Drift Alerts"], ["product_drift", "Product Drift Alerts"],
];

const Toggle = ({ on, onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} className="relative w-11 h-6 rounded-full transition-colors" style={{ background: on ? "#39ff14" : "#2a2a30" }}>
    <span className="absolute top-0.5 w-5 h-5 rounded-full bg-black transition-all" style={{ left: on ? "22px" : "2px" }} />
  </button>
);

function NotificationPrefs({ user, refreshUser }) {
  const [prefs, setPrefs] = useState(user?.notif_prefs || {});
  useEffect(() => { setPrefs(user?.notif_prefs || {}); }, [user]);
  const save = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await api.put("/account/preferences", { notif_prefs: { [key]: next[key] } }); await refreshUser(); toast.success("Preference saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <HoloCard accent="turquoise" className="p-6 max-w-xl" hover={false} testid="tab-notifications">
      <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">Email Notifications</div>
      <div className="space-y-4">
        {TOGGLES.map(([k, label]) => (
          <div key={k} className="flex items-center justify-between">
            <span className="font-mono-tech text-sm text-white/75">{label}</span>
            <Toggle on={prefs[k] !== false} onClick={() => save(k)} testid={`notif-toggle-${k}`} />
          </div>
        ))}
      </div>
      <p className="font-mono-tech text-[10px] text-white/30 mt-5">In-app notifications always remain active. These control email delivery only.</p>
    </HoloCard>
  );
}

function SettingsTab({ user, refreshUser }) {
  const [s, setS] = useState(user?.settings || {});
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setS(user?.settings || {}); }, [user]);

  const saveSetting = async (key, val) => {
    const next = { ...s, [key]: val };
    setS(next);
    try { await api.put("/account/preferences", { settings: { [key]: val } }); await refreshUser(); toast.success("Setting saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const changePassword = async () => {
    if (pw.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    setSaving(true);
    try { await api.post("/account/password", pw); setPw({ current_password: "", new_password: "" }); toast.success("Password changed"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="tab-settings">
      <HoloCard accent="purple" className="p-6" hover={false}>
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">Preferences</div>
        <div className="space-y-4">
          <div className="flex items-center justify-between"><span className="font-mono-tech text-sm text-white/75 flex items-center gap-2"><Volume2 size={15} /> Sound Effects</span><Toggle on={s.sound !== false} onClick={() => saveSetting("sound", s.sound === false)} testid="setting-sound" /></div>
          <div className="flex items-center justify-between"><span className="font-mono-tech text-sm text-white/75 flex items-center gap-2"><Shield size={15} /> Share Anonymous Analytics</span><Toggle on={s.privacy_analytics !== false} onClick={() => saveSetting("privacy_analytics", s.privacy_analytics === false)} testid="setting-privacy" /></div>
          <div>
            <span className="font-mono-tech text-sm text-white/75 flex items-center gap-2 mb-2"><Palette size={15} /> Theme</span>
            <select value={s.theme || "cyberpunk"} onChange={(e) => saveSetting("theme", e.target.value)} data-testid="setting-theme" className="w-full bg-black/40 border border-white/15 px-3 py-2 text-white font-mono-tech text-sm">
              <option value="cyberpunk" className="bg-[#0d0d0f]">Luxury Cyberpunk (default)</option>
              <option value="neon_noir" className="bg-[#0d0d0f]">Neon Noir</option>
              <option value="matrix" className="bg-[#0d0d0f]">Matrix Green</option>
            </select>
          </div>
        </div>
      </HoloCard>
      <HoloCard accent="pink" className="p-6" hover={false}>
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4 flex items-center gap-2"><Lock size={13} /> Security</div>
        <label className="font-mono-tech text-[10px] uppercase text-white/40">Current Password</label>
        <input type="password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} data-testid="pw-current" className="mt-1 mb-3 w-full bg-black/40 border border-white/15 px-3 py-2 text-white font-mono-tech text-sm" />
        <label className="font-mono-tech text-[10px] uppercase text-white/40">New Password</label>
        <input type="password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} data-testid="pw-new" className="mt-1 w-full bg-black/40 border border-white/15 px-3 py-2 text-white font-mono-tech text-sm" />
        <NeonButton accent="pink" className="mt-4" onClick={changePassword} disabled={saving || !pw.current_password || !pw.new_password} data-testid="pw-change-btn">
          {saving ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : <><Check size={13} /> Change Password</>}
        </NeonButton>
        <p className="font-mono-tech text-[10px] text-white/30 mt-4 flex items-center gap-1.5"><Shield size={11} /> Two-Factor Authentication — coming soon</p>
      </HoloCard>
      <div className="lg:col-span-2"><SessionsPanel /></div>
    </div>
  );
}

function SessionsPanel() {
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/auth/sessions").then(({ data }) => setSessions(data)).catch(() => setSessions([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    setBusy(true);
    try { await api.post(`/auth/sessions/${id}/revoke`); toast.success("Session revoked"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const revokeAll = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/sessions/revoke-all"); toast.success(`Signed out ${data.revoked} other device(s)`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const parseDevice = (ua) => {
    if (!ua) return "Unknown device";
    const os = /Windows/i.test(ua) ? "Windows" : /Mac|iPhone|iPad/i.test(ua) ? "Apple" : /Android/i.test(ua) ? "Android" : /Linux/i.test(ua) ? "Linux" : "Device";
    const br = /Edg/i.test(ua) ? "Edge" : /Chrome/i.test(ua) ? "Chrome" : /Firefox/i.test(ua) ? "Firefox" : /Safari/i.test(ua) ? "Safari" : "Browser";
    return `${br} · ${os}`;
  };

  return (
    <HoloCard accent="turquoise" className="p-6" hover={false} testid="sessions-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/50 flex items-center gap-2"><Monitor size={13} /> Active Sessions & Devices</div>
        <div className="flex gap-2">
          <NeonButton accent="turquoise" onClick={load} disabled={busy} data-testid="sessions-refresh-btn"><RefreshCw size={12} /> Refresh</NeonButton>
          <NeonButton accent="pink" onClick={revokeAll} disabled={busy || !(sessions?.length > 1)} data-testid="sessions-revoke-all-btn"><LogOut size={12} /> Sign out others</NeonButton>
        </div>
      </div>
      {sessions === null ? (
        <div className="font-mono-tech text-white/30 text-sm flex items-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading sessions…</div>
      ) : sessions.length === 0 ? (
        <div className="font-mono-tech text-white/30 text-sm">No active sessions.</div>
      ) : (
        <div className="space-y-3">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center gap-3 border border-white/10 px-4 py-3" data-testid={`session-${s.id}`}>
              <Monitor size={18} className={s.current ? "text-neon-turquoise" : "text-white/40"} />
              <div className="flex-1 min-w-0">
                <div className="font-mono-tech text-sm text-white/85 flex items-center gap-2">
                  {parseDevice(s.ua)}
                  {s.current && <span className="font-mono-tech text-[9px] uppercase px-1.5 py-0.5 bg-neon-turquoise/15 text-neon-turquoise">This device</span>}
                </div>
                <div className="font-mono-tech text-[10px] text-white/35 truncate">{s.ip || "—"} · active {fmtDateTime(s.last_seen)}</div>
              </div>
              {!s.current && (
                <button onClick={() => revoke(s.id)} disabled={busy} data-testid={`session-revoke-${s.id}`}
                  className="text-white/40 hover:text-neon-pink transition-colors shrink-0"><Trash2 size={16} /></button>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="font-mono-tech text-[10px] text-white/30 mt-4 flex items-center gap-1.5"><Shield size={11} /> Revoking a session signs that device out immediately.</p>
    </HoloCard>
  );
}
